import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useForm } from "react-hook-form";
import {
  Button,
  Checkbox,
  FieldError,
  FlexRow,
  Form,
  Input,
  Label,
  Legend,
  Link,
  LoadingSpinner,
} from "atoms";
import type { User } from "features/auth";
import { useAuth } from "features/auth";
import type { UploadedImage } from "features/images";
import { ImageUpload } from "features/images";
import { ApiError } from "utils/api";
import { useUpdateProfile } from "../api/useProfile";

const userInterests = [
  "Learn more about Web3, NFTs, and digital collectibles",
  "Get discovered by new and existing collectors",
  "Discover interesting creators and collections",
  "Follow, connect, and stay updated with your favorite creators",
  "Share your love for your favorite creators",
  "Suggest and curate new creators to add to HUG",
];

type ProfileFields = Pick<
  User,
  "name" | "avatar" | "userInterests" | "profileCompletedAt"
>;

function EditProfileDetails() {
  // Grab user profile data from Auth context, Router ensures they're already signed in
  const { user } = useAuth();

  const { updateProfile, isLoading, isSuccess, isError, error } =
    useUpdateProfile();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    reset,
  } = useForm<ProfileFields>({
    defaultValues: {
      ...user,
    },
  });

  const onSubmit = (data: ProfileFields) => {
    // If the user hasn't completed their profile, update with current timestamp
    const profileCompletedAt =
      user?.profileCompletedAt ?? new Date().toISOString();

    // If avatar object is default value, replace with undefined to keep optional
    const avatar = data.avatar?.url === "" ? undefined : data.avatar;

    return updateProfile(
      {
        ...data,
        avatar,
        profileCompletedAt,
      },
      {
        // If successful, update form default values with new user response to tell if any fields
        // are dirty _after_ submitting
        onSuccess: (resp) => reset({ ...resp.user }),
      }
    );
  };

  // Set avatar fields on upload
  const setUploadFields = ({ url, file }: UploadedImage) => {
    setValue("avatar.url", url, { shouldDirty: true });
    setValue("avatar.contentType", file.type);
    setValue("avatar.importDate", new Date().toISOString());
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <fieldset>
        <Legend>Avatar</Legend>

        <ImageUpload
          fieldRegister={register("avatar.url")}
          onSuccess={setUploadFields}
          errorMessage={errors?.avatar?.url?.message}
          src={user?.avatar?.url}
          entityType="ARTIST"
        />
      </fieldset>

      <Label>
        Username
        <Input {...register("name")} />
        {errors.name && <FieldError>{errors.name.message}</FieldError>}
      </Label>

      <Label as="fieldset">
        <Legend>Your Interests</Legend>

        {userInterests.map((interest) => (
          <Checkbox
            key={interest}
            {...register("userInterests")}
            label={interest}
            value={interest}
          />
        ))}

        {errors.userInterests && (
          <FieldError>{errors.userInterests?.message}</FieldError>
        )}
      </Label>

      <FlexRow>
        <Button
          type="submit"
          variant="gradient"
          size="md"
          disabled={isLoading || !isDirty}
        >
          Save
        </Button>

        <Link to="/profile" variant="primary" size="md">
          Cancel
        </Link>
      </FlexRow>

      {isSuccess && <Success>Changes Saved!</Success>}

      {isError && (
        <FieldError>
          {error instanceof ApiError ? error.message : "Something went wrong."}
        </FieldError>
      )}

      {isLoading && <LoadingSpinner />}
    </Form>
  );
}

const disappear = keyframes`
  0% {
    opacity: 1;
  }
  99% {
    opacity: 0;
  }
  100% {
    visibility: hidden;
  }
`;

const Success = styled.div({
  animation: `1s ease-in 2s forwards ${disappear}`,
  // animationDelay: "2s",
  // animationFillMode: "forwards",
});

export { EditProfileDetails };
